import type { ValidationIssue } from '@customerio/dryad';
import { TYPE_MAP } from './named-bitmap';

/**
 * The shared UI/state contract for the message condition as the people-search
 * wire speaks it.
 *
 * `eventName` uses the message-value grammar as the pickers speak it: `'*'`
 * (any email), `'push_*'` (any push), `'output_11'`, `'newsletter_3'`,
 * `'any_email-campaign_4'` (any email in campaign 4)... `eventType` is a
 * `TYPE_MAP` key (`sent_email`, `opened_action`, ...). Wire shapes do NOT live
 * here: the people-search proto wire collapses everything into one base64
 * `named_bitmap` name, and the format plugin owns that encoding — including
 * the `any_` strip/restore pair below, which must apply at the plugin's wire
 * edges.
 */
export type MessageFilterConditionState = {
  eventName: string;
  eventType: string;
  human: boolean;
  inverse: boolean;
};

/**
 * The `any_` prefix on a per-campaign wildcard ("any email in campaign 4") is
 * UI-only: the pickers offer `any_email-campaign_4`, but the name the backend
 * indexes is `email-campaign_4` (services core/events/index.go) — a name
 * serialized with the prefix intact addresses a bitmap that never exists and
 * matches nobody (AUD-4375). Builder state carries the prefixed picker
 * grammar, so the message plugin must strip through {@link toWireMessageName}
 * on serialize and restore through {@link toPickerMessageName} on parse.
 */
export function toWireMessageName(eventName: string): string {
  if (eventName.startsWith('any_')) {
    return eventName.slice('any_'.length);
  }
  return eventName;
}

/**
 * Only campaign-wildcard names (`<channel>-campaign_<id>`) ever carry the
 * prefix, and a name that already has one — a shape only this plugin family
 * briefly wrote during the AUD-4375 window — is kept as-is, so the next
 * serialize repairs it.
 */
export function toPickerMessageName(eventName: string): string {
  if (eventName.includes('-campaign') && !eventName.startsWith('any_')) {
    return `any_${eventName}`;
  }
  return eventName;
}

/**
 * The channel a message value in the picker grammar refers to: `push_5` →
 * `push`, `any_email-campaign_4` → `email`, `iac_tmpl_7` → `in_app`,
 * `push_newsletter_5` → `push` (non-email broadcast messages carry a
 * `<channel>_newsletter_<id>` output id — services ui_api/deliveries/
 * filters.go). Kept in one place so every consumer derives the same channel
 * (AUD-4378). Values it can't decode (language-branch `output_*` names,
 * plain-email `newsletter_<id>` names) return a non-channel string; callers
 * decide the fallback.
 */
export function messageChannelFromName(eventName: string): string {
  const channel = eventName
    .replace('any_', '')
    .replace('-campaign', '')
    .replace(/_[^_]+$/, '')
    .replace('iac_tmpl', 'in_app') // iac_tmpl_$id
    .replace('iac', 'in_app'); // iac_$id

  if (channel === 'sms' || channel.startsWith('sms_')) {
    return 'sms';
  }

  if (channel.endsWith('_newsletter')) {
    return channel.slice(0, channel.indexOf('_newsletter'));
  }

  return channel;
}

/**
 * The event types the message row edits: message activity metrics only. The
 * same `named_bitmap` leaf / `TYPE_MAP` vocabulary also encodes behavioral
 * types (`event`, `page`, `attribute_change`, `device_change`, ...) that
 * belong to other condition editors, so the message plugin must decline them
 * or a saved event condition would render as a message row with an impossible
 * channel.
 */
export function isMessageEventType(eventType: string): boolean {
  return Object.hasOwn(TYPE_MAP, eventType) && /_(email|action|content)$/.test(eventType);
}

/**
 * A fresh row has no message picked; committing it would encode a name the
 * backend can't resolve, so the host's commit gate blocks on this until the
 * picker is used.
 */
export function validateMessageFilter(state: MessageFilterConditionState): Required<ValidationIssue>[] {
  if (state.eventName === '') {
    return [{ message: 'Select a message', field: 'eventName' }];
  }
  return [];
}
